import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import adminApi from "../../APIs/admin";
import printerApi from "../../APIs/printer";
import { useRoot } from "../../context/RootProvider";
import {
  Button,
  PageHeader,
  Pagination,
  Table,
  Typography,
} from "../../designComponents";
import { TableDataType } from "../../designComponents/Table";
import Store from "../../store";
import { RootState } from "../../store/selectors";
import theme from "../../theme";
import { PrinterClientType } from "../../types";
import { requestAPI } from "../../utils";
import { MyParamsType } from "../Building";
import { ClientJobContainerStyled, ClientJobStyled } from "./style";

type ParamsType = {
  page: number;
  limit: number;
};

const PrinterClientList = () => {
  const dispatch = useDispatch();

  const clientPrinter = useSelector(
    (state: RootState) => state.PrinterClientList.data,
  );
  const clients = useSelector((state: RootState) => state.clientData.data);
  const pagination = useSelector(
    (state: RootState) => state.clientData.pagination,
  );

  const [search, setSearch] = useState("");

  const getClients = async (pageParams: MyParamsType) => {
    try {
      await requestAPI(
        adminApi.getUserByRole({
          page: pageParams.page,
          limit: pageParams.limit,
          search: pageParams.search ? pageParams.search : "",
          role: "CLIENT",
        }),
      );
    } catch (error: any) {
      console.error(error);
      toast.error(error);
    }
  };

  const navigate = useNavigate();
  const { auth } = useRoot();
  const role = auth?.role;
  const formatTableData = () => {
    const formattedData = clients.map((item) => ({
      id: item?._id,
      clientName: item?.fullName,
      emailAddress: item?.user?.emailAddress,
      address:
        item?.contactInfo?.address1?.concat(", ", item.contactInfo.city) ??
        "Tinkune, Kathmandu",
      phoneNumber: item?.user?.role,
      actions: (
        <Button
          variant="secondary"
          onClick={() => navigate(`/printer-clients/${item._id}`)}
          style={{
            backgroundColor: theme.colors.blueLight,
            border: `1px solid ${theme.colors.blueLight}`,
          }}
        >
          View Details
        </Button>
      ),
    }));

    return formattedData;
  };

  const fetchClient = async () => {
    try {
      await requestAPI(printerApi.getAllPrinterClient());
    } catch (error) {
      console.error(error);
    }
  };

  const clientList = clientPrinter.map((client: PrinterClientType) => {
    return {
      id: client?._id || "",
      clientName: client.fullName,
      emailAddress: client.emailAddress,
      address: client.contactInfo.address1 + ", " + client.contactInfo.address2,
      shippingAddress: client.shippingAddress || "Tinkune , Kathmandu",
      phoneNumber: (
        <div>
          <img
            src="https://media.istockphoto.com/id/982533982/vector/simple-flag-correct-size-proportion-colors.jpg?s=1024x1024&w=is&k=20&c=67JOZZofEraeagglFPvjdJ9EIwFXKzp0oweDgHsdlXg="
            alt="country"
            className="flag"
          />
          <span>{client.mobileNumber}</span>
        </div>
      ),
      actions: (
        <Button
          variant="secondary"
          onClick={() => navigate(`${client._id}`)}
          style={{
            backgroundColor: theme.colors.blueLight,
            border: `1px solid ${theme.colors.blueLight}`,
          }}
        >
          View Details
        </Button>
      ),
    };
  });
  const roleCheck = () => {
    return auth?.role === "ADMIN" ? getClients(pagination) : fetchClient();
  };

  useEffect(() => {
    roleCheck();
  }, []);

  const tableData: TableDataType = {
    headings: [
      { key: "clientName", label: "Client Name" },
      { key: "emailAddress", label: "Email Address" },
      { key: "address", label: "Address" },
      // { key: 'shippingAddress', label: 'Shipping Address' },
      { key: "phoneNumber", label: "Role" },
      // { key: 'companyAddress', label: 'Company Address' },
      { key: "actions", label: "Actions" },
    ],

    body: role === "ADMIN" ? formatTableData() : clientList,
  };
  const handlePagination = (updatedParams: ParamsType) => {
    getClients(updatedParams);
    dispatch(Store.Actions.update("clientData.pagination", updatedParams));
  };

  const debouncedFunction = useCallback(
    debounce((myParams) => getClients(myParams), 500),
    [],
  );

  const handleOnSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    debouncedFunction({ ...pagination, page: 1, search: value });
  };

  return (
    <ClientJobContainerStyled>
      <PageHeader
        pageTitle={<Typography variant="title">Clients</Typography>}
        handleOnSearchTextChange={handleOnSearchTextChange}
        search={search}
      />

      <ClientJobStyled>
        <div className="table-container">
          <Table tableData={tableData} maxWidthForCardView="800px" />
        </div>
        <Pagination
          activePage={pagination.page}
          handlePagination={handlePagination}
          limit={pagination.limit}
          totalCount={pagination.totalData || 0}
        />
      </ClientJobStyled>
    </ClientJobContainerStyled>
  );
};

export default PrinterClientList;
